import type { MonitorEventItem } from '@/lib/api'
import { getCountryLatLng } from './countryCoords'

/** Simple deterministic integer hash of a string (djb2). */
function hashStr(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = (Math.imul(h, 0x01000193) >>> 0)
  }
  return h
}

function key(lat: number, lng: number): string {
  return `${lat.toFixed(3)}_${lng.toFixed(3)}`
}

/**
 * Returns a map of event id → { lat, lng } with deterministic spread applied.
 * Events that share the same coordinates (e.g. same country centroid) are spread
 * in a loose spiral / ring pattern around the country center so they don't sit
 * on a single line.
 */
export function getSpreadEventCoordinates(
  events: MonitorEventItem[]
): Map<string, { lat: number; lng: number }> {
  const raw: { event: MonitorEventItem; lat: number; lng: number }[] = []
  for (const e of events) {
    let lat = e.latitude
    let lng = e.longitude
    if (lat == null || lng == null) {
      const coords = getCountryLatLng(e.country_code)
      if (!coords) continue
      lat = coords[0]
      lng = coords[1]
    }
    raw.push({ event: e, lat, lng })
  }

  const byPos = new Map<string, typeof raw>()
  for (const r of raw) {
    const k = key(r.lat, r.lng)
    if (!byPos.has(k)) byPos.set(k, [])
    byPos.get(k)!.push(r)
  }

  const result = new Map<string, { lat: number; lng: number }>()

  for (const group of byPos.values()) {
    if (group.length <= 1) {
      result.set(group[0].event.id, { lat: group[0].lat, lng: group[0].lng })
      continue
    }
    const lat0 = group[0].lat
    const lng0 = group[0].lng
    const n = group.length
    const maxRadiusDeg = Math.min(8, 2 + n * 0.12)
    const lat0Rad = (lat0 * Math.PI) / 180

    group.forEach((r) => {
      // Deterministic rectangular spread — avoids the circular ring artifact
      // from equal-area polar distribution when hundreds of events share one centroid
      const h = hashStr(r.event.id)
      const normLat = ((h & 0xffff) / 0xffff) * 2 - 1         // -1 to 1
      const normLng = (((h >>> 16) & 0xffff) / 0xffff) * 2 - 1 // -1 to 1
      const offsetLat = normLat * maxRadiusDeg
      const offsetLng = (normLng * maxRadiusDeg) / Math.max(0.1, Math.cos(lat0Rad))
      result.set(r.event.id, { lat: lat0 + offsetLat, lng: lng0 + offsetLng })
    })
  }
  return result
}
